import { useQuery } from '@tanstack/react-query';
import { ArrowLeftIcon } from 'lucide-react';
import { useState, useMemo, useEffect } from 'react';

import { Avatar, AvatarImage } from './ui/avatar';
import { Skeleton } from './ui/skeleton';
import { TooltipContent, TooltipTrigger } from './ui/tooltip';
import { Tooltip } from './ui/tooltip';

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogCancel,
  AlertDialogAction,
  AlertDialogHeader,
  AlertDialogFooter,
} from '@/components/ui/alert-dialog';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { usePullMutations } from '@/features/pulls/services/pulls';
import { branchesQuery } from '@/services/repo';

export function BranchNames({
  author,
  base,
  head,
  owner,
  repo,
  pull_number,
}: {
  owner: string;
  repo: string;
  pull_number: number;
  author: { avatar_url: string; login: string };
  base: string;
  head: string;
}) {
  const [showCopied, setShowCopied] = useState(false);
  const [open, setOpen] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingBranch, setPendingBranch] = useState<string>();

  const { data: branches, isLoading: branchesLoading } = useQuery(branchesQuery(owner, repo));
  const { updateBase } = usePullMutations(owner, repo, pull_number);

  const otherBranches = useMemo(
    () => branches?.filter((branch) => branch.name !== base && branch.name !== head) ?? [],
    [branches, base, head]
  );

  useEffect(() => {
    if (showCopied) {
      setTimeout(() => {
        setShowCopied(false);
      }, 1500);
    }
  }, [showCopied]);

  return (
    <div className="flex items-center gap-1 w-fit justify-end">
      {/* Author and branch names */}
      <div className="flex items-center gap-1 text-muted-foreground">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <div className="flex items-center px-1.5 py-1 cursor-pointer border-stone-200 border hover:bg-stone-300 rounded-md">
              {/* <ChevronDownIcon size={12} className="m-1" /> */}
              <span className="text-xs">{base}</span>
            </div>
          </PopoverTrigger>
          <PopoverContent className="w-[200px] p-0">
            <Command>
              <CommandInput placeholder="Search branches..." className="text-sm" />
              <CommandList>
                <CommandEmpty>No branch found.</CommandEmpty>
                <CommandGroup>
                  {branchesLoading &&
                    Array.from({ length: 10 }).map((_, index) => (
                      <CommandItem key={index} className="text-sm">
                        <Skeleton className="h-4 w-full" />
                      </CommandItem>
                    ))}
                  {!branchesLoading &&
                    otherBranches.map((branch) => (
                      <CommandItem
                        key={branch.name}
                        value={branch.name}
                        onSelect={(currentValue) => {
                          setPendingBranch(currentValue);
                          setShowConfirm(true);
                          setOpen(false);
                        }}
                        className="text-sm"
                      >
                        {branch.name}
                      </CommandItem>
                    ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        <ArrowLeftIcon size={12} className="ml-1" />
        <Tooltip open={showCopied}>
          <TooltipTrigger asChild>
            <div
              className="flex items-center overflow-hidden py-1 px-1.5 cursor-pointer hover:bg-stone-200 rounded-md group"
              onClick={() => {
                navigator.clipboard.writeText(head).then(() => {
                  setShowCopied(true);
                });
              }}
            >
              <span className="text-xs truncate">{head}</span>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p>Copied!</p>
          </TooltipContent>
        </Tooltip>
        <Tooltip delayDuration={700}>
          <TooltipTrigger asChild>
            <Avatar className="size-6">
              <AvatarImage src={author.avatar_url} alt={author.login} />
            </Avatar>
          </TooltipTrigger>
          <TooltipContent>
            <p>{author.login}</p>
          </TooltipContent>
        </Tooltip>
      </div>

      <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Change base branch to <code className="text-primary">{pendingBranch}</code>?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to change the base branch from <code className="text-primary">{base}</code> to{' '}
              <code className="text-primary">{pendingBranch}</code>? This will update the comparison and may affect the
              changes shown in the pull request.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pendingBranch) {
                  updateBase({ newBase: pendingBranch, onSettled: () => setShowConfirm(false) });
                }
              }}
            >
              Change branch
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
